/**
 * The estimate questions (a category's time, a book's time per page) are asked in the session's popup when the click
 * that brought them came from it: Mark complete there, or ✓ on a reading. The popup turns into the question rather than
 * an overlay opening over it (Petr, 2026-10-31). The popup says here that it is waiting for its save's answer; the save
 * says whether it has a question, and asks in the popup only if that is still open and waiting. Otherwise the overlay
 * asks, once what the click set moving is still (msUntilStill).
 */
type Answer = (asked: boolean) => void;

const waiting = new Map<string, Answer>();

/** The popup of `sessionId` waits for its save's answer; `answer` hears whether a question came. Returns a stop. */
export const waitForQuestion = (sessionId: number | string, answer: Answer) => {
  const key = String(sessionId);
  waiting.set(key, answer);
  return () => {
    if (waiting.get(key) === answer) waiting.delete(key);
  };
};

export const isWaitingForQuestion = (sessionId?: number | string) =>
  sessionId !== undefined && waiting.has(String(sessionId));

/** Tells the waiting popup whether its save brought a question; false when no popup was waiting. */
export const answerWaitingPopup = (sessionId: number | string | undefined, asked: boolean) => {
  if (sessionId === undefined) return false;
  const key = String(sessionId);
  const answer = waiting.get(key);
  if (!answer) return false;
  waiting.delete(key);
  answer(asked);
  return true;
};

/**
 * How long after a question appears a click outside it does nothing rather than answer "No": students go fast, and a
 * click already on its way to the next thing shouldn't dismiss it unread (2026-10-31).
 */
export const DISMISS_HOLD_MS = 1000;

// The popup showing a question, for a click outside it to count as "No" (Petr, 2026-10-31) rather than just closing it.
let dismissAsking: (() => void) | null = null;

export const onDismissAsking = (dismiss: () => void) => {
  dismissAsking = dismiss;
  return () => {
    if (dismissAsking === dismiss) dismissAsking = null;
  };
};

/** A click outside the popup while it asks: answered "No". True when there was a question to answer. */
export const dismissAskingPopup = () => {
  if (!dismissAsking) return false;
  dismissAsking();
  return true;
};
